import React, { useState, useEffect } from "react";
import { Form, InputGroup, Button, ListGroup } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleRight, faCheck, faExclamationTriangle, faTimes, faSearch } from "@fortawesome/free-solid-svg-icons";
import "./CustomCascader.css";
import "react-loading-skeleton/dist/skeleton.css";

interface Category {
  id: string;
  name: string;
  level: number;
  include_in_menu?: number; // ✅ Ensure this is defined
  children?: Category[];
}

interface CustomCascaderProps {
  fetchCategories: () => Promise<Category[]>; // Fetch top-level categories
  onCategorySelected: (selectedCategory: {
    level2: Category | null;
    level3: Category | null;
    level4: Category | null;
  }) => void;
  value: string;
}

const CustomCascader: React.FC<CustomCascaderProps> = ({
  fetchCategories,
  onCategorySelected,
  value
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<{
    level2: Category | null;
    level3: Category | null;
    level4: Category | null;
  }>({ level2: null, level3: null, level4: null });

  // State to manage validation
  const [validated, setValidated] = useState(false);
  const [showGuidance, setShowGuidance] = useState(false);
  const [recentlyReset, setRecentlyReset] = useState<"level3" | "level4" | null>(null);

  // This state will store the category path names for display
  const [categoryNames, setCategoryNames] = useState<{[key: string]: string}>({});
  
  // Store category IDs separately to avoid TypeScript errors
  const [categoryIds, setCategoryIds] = useState<{
    level2: string | null;
    level3: string | null;
    level4: string | null;
  }>({ level2: null, level3: null, level4: null });

  // Global search state
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  const [globalSearchResults, setGlobalSearchResults] = useState<Array<{
    category: Category;
    level: number;
    path: Category[];
  }>>([]);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);

  // Listen for ESC key to prevent it from closing the dropdown
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dropdownOpen) {
        e.preventDefault();
        e.stopPropagation();
        
        // Only close if validated or no selection
        if (validated || (!selectedCategories.level2 && !selectedCategories.level3 && !selectedCategories.level4)) {
          setDropdownOpen(false);
          setSearchTerm(["", "", ""]);
          setGlobalSearchTerm("");
          setShowGlobalSearch(false);
          setShowGuidance(false);
        } else {
          // Show guidance instead of closing
          setShowGuidance(true);
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dropdownOpen, validated, selectedCategories]);

  // Add event listener for product publish event to completely reset the component
  useEffect(() => {
    const handleProductPublished = () => {
      console.log("[CustomCascader] Product published event received, resetting completely");
      // Force a complete reset of all category data
      setCategoryIds({ level2: null, level3: null, level4: null });
      setCategoryNames({});
      setSelectedPath([]);
      setSelectedCategories({ level2: null, level3: null, level4: null });
      setValidated(false);
      setGlobalSearchTerm("");
      setShowGlobalSearch(false);
    };
    
    document.addEventListener('product-published', handleProductPublished);
    
    return () => {
      document.removeEventListener('product-published', handleProductPublished);
    };
  }, []);

  // Load initial selection from value prop
  useEffect(() => {
    console.log("[CustomCascader] Initial value:", value);
    
    // Check if value is empty or represents an empty category
    if (!value || value === "{}" || value === '{"level2":null,"level3":null,"level4":null}') {
      // Reset all category selections
      setCategoryIds({ level2: null, level3: null, level4: null });
      setCategoryNames({});
      setSelectedPath([]);
      setSelectedCategories({ level2: null, level3: null, level4: null });
      console.log("[CustomCascader] Cleared all category selections");
      return;
    }
    
    try {
      const parsedValue = JSON.parse(value);
      console.log("[CustomCascader] Parsed value:", parsedValue);
      
      // Set as initial selection
      if (parsedValue && typeof parsedValue === 'object') {
        // Store just the IDs
        const newCategoryIds = {
          level2: parsedValue.level2?.id || null,
          level3: parsedValue.level3?.id || null,
          level4: parsedValue.level4?.id || null,
        };
        console.log("[CustomCascader] Setting category IDs:", newCategoryIds);
        setCategoryIds(newCategoryIds);
        
        // Also store category names
        if (parsedValue.level2?.name) {
          setCategoryNames(prev => ({
            ...prev,
            level2: parsedValue.level2.name
          }));
        }
        
        if (parsedValue.level3?.name) {
          setCategoryNames(prev => ({
            ...prev,
            level3: parsedValue.level3.name
          }));
        }
        
        if (parsedValue.level4?.name) {
          setCategoryNames(prev => ({
            ...prev,
            level4: parsedValue.level4.name
          }));
        }
        
        // Update the selected path based on the names we now know
        const pathNames = [];
        if (parsedValue.level2?.name) pathNames.push(parsedValue.level2.name);
        if (parsedValue.level3?.name) pathNames.push(parsedValue.level3.name);
        if (parsedValue.level4?.name) pathNames.push(parsedValue.level4.name);
        setSelectedPath(pathNames);
        
        // We'll load the full category objects when categories are loaded
      }
    } catch (error) {
      console.error("Error parsing category value:", error);
    }
  }, [value]);

  const [categories, setCategories] = useState<Category[]>([]);
  const [levelTwoCategories, setLevelTwoCategories] = useState<Category[]>([]);
  const [levelThreeCategories, setLevelThreeCategories] = useState<Category[]>(
    []
  );

  const [searchTerm, setSearchTerm] = useState(["", "", ""]);
  const [loading, setLoading] = useState({
    level2: false,
    level3: false,
    level4: false,
  });

  // Add overall loading state
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);

  // Function to recursively search all categories
  const searchAllCategories = (categories: Category[], searchTerm: string, parentPath: Category[] = []): Array<{
    category: Category;
    level: number;
    path: Category[];
  }> => {
    const results: Array<{
      category: Category;
      level: number;
      path: Category[];
    }> = [];

    categories.forEach(category => {
      const currentPath = [...parentPath, category];
      
      // Check if current category matches search
      if (category.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        results.push({
          category,
          level: category.level,
          path: currentPath
        });
      }

      // Recursively search children
      if (category.children && category.children.length > 0) {
        const childResults = searchAllCategories(category.children, searchTerm, currentPath);
        results.push(...childResults);
      }
    });

    return results;
  };

  // Function to build the complete category path from a selected category
  const buildCategoryPath = (selectedCategory: Category, searchResults: Array<{
    category: Category;
    level: number;
    path: Category[];
  }>): {
    level2: Category | null;
    level3: Category | null;
    level4: Category | null;
  } => {
    // Find the result that matches our selected category
    const result = searchResults.find(r => r.category.id === selectedCategory.id);
    if (!result) {
      return { level2: null, level3: null, level4: null };
    }

    const path = result.path;
    const categorySelection: {
      level2: Category | null;
      level3: Category | null;
      level4: Category | null;
    } = { level2: null, level3: null, level4: null };

    // Extract categories by level from the path (using correct database level mapping)
    path.forEach(cat => {
      if (cat.level === 2) {
        categorySelection.level2 = cat;
      } else if (cat.level === 3) {
        categorySelection.level3 = cat;
      } else if (cat.level === 4) {
        categorySelection.level4 = cat;
      }
    });

    // We need at least level2 (which corresponds to database level 2)
    if (!categorySelection.level2) {
      console.warn("Invalid category selection - missing required level2");
      return { level2: null, level3: null, level4: null };
    }

    return categorySelection;
  };

  // Handle global search selection
  const handleGlobalSearchSelection = (searchResult: {
    category: Category;
    level: number;
    path: Category[];
  }) => {
    console.log("Global search selection:", searchResult);
    
    // Build complete category selection from path
    const categorySelection = buildCategoryPath(searchResult.category, globalSearchResults);
    
    if (categorySelection.level2) {
      // Update all states
      setSelectedCategories(categorySelection);
      
      // Update category IDs
      setCategoryIds({
        level2: categorySelection.level2.id,
        level3: categorySelection.level3?.id || null,
        level4: categorySelection.level4?.id || null
      });
      
      // Update category names
      const newCategoryNames: any = {
        level2: categorySelection.level2.name,
      };
      if (categorySelection.level3) {
        newCategoryNames.level3 = categorySelection.level3.name;
      }
      if (categorySelection.level4) {
        newCategoryNames.level4 = categorySelection.level4.name;
      }
      setCategoryNames(newCategoryNames);
      
      // Update path for display
      const pathNames = [categorySelection.level2.name];
      if (categorySelection.level3) {
        pathNames.push(categorySelection.level3.name);
      }
      if (categorySelection.level4) {
        pathNames.push(categorySelection.level4.name);
      }
      setSelectedPath(pathNames);
      
      // Update sub-categories for cascader navigation
      setLevelTwoCategories(categorySelection.level2.children || []);
      if (categorySelection.level3) {
        setLevelThreeCategories(categorySelection.level3.children || []);
      }
      
      // Clear global search
      setGlobalSearchTerm("");
      setShowGlobalSearch(false);
      
      // Reset validation state
      setValidated(false);
      setShowGuidance(false);
      
      // Notify parent component
      onCategorySelected(categorySelection);
      
      console.log("Auto-selected category path:", pathNames);
    }
  };

  // Handle global search input change
  const handleGlobalSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setGlobalSearchTerm(searchValue);
    
    if (searchValue.length >= 2) {
      // Perform search across all categories
      const results = searchAllCategories(categories, searchValue);
      setGlobalSearchResults(results);
      setShowGlobalSearch(true);
    } else {
      setGlobalSearchResults([]);
      setShowGlobalSearch(false);
    }
  };

  // Fetch initial categories
  useEffect(() => {
    const loadCategories = async () => {
      setIsLoadingInitial(true);
      setLoading((prev) => ({ ...prev, level2: true }));
      let fetchedCategories: Category[] = [];
      try {
        fetchedCategories = await fetchCategories();
        setCategories(fetchedCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setIsLoadingInitial(false);
        setLoading((prev) => ({ ...prev, level2: false }));
      }
      
      // If there's a selected level2 category (by ID), find the full category object
      if (categoryIds.level2) {
        const level2Category = fetchedCategories.find((cat: Category) => cat.id === categoryIds.level2);
        if (level2Category) {
          // Update the selected category with the full object
          setSelectedCategories(prev => ({
            ...prev,
            level2: level2Category
          }));
          
          // Add the name to our path
          setCategoryNames(prev => ({
            ...prev,
            level2: level2Category.name
          }));
          
          // Set the level 2 categories for the dropdown
          setLevelTwoCategories(level2Category.children || []);
          
          // If there's a selected level3 category, load its children too
          if (categoryIds.level3 && level2Category.children) {
            const level3Category = level2Category.children.find((cat: Category) => cat.id === categoryIds.level3);
            if (level3Category) {
              // Update level3 with full object
              setSelectedCategories(prev => ({
                ...prev,
                level3: level3Category
              }));
              
              // Add name to our path
              setCategoryNames(prev => ({
                ...prev,
                level3: level3Category.name
              }));
              
              setLevelThreeCategories(level3Category.children || []);
              
              // If there's a level4 category selected by ID
              if (categoryIds.level4 && level3Category.children) {
                const level4Category = level3Category.children.find((cat: Category) => cat.id === categoryIds.level4);
                if (level4Category) {
                  // Update with full object
                  setSelectedCategories(prev => ({
                    ...prev,
                    level4: level4Category
                  }));
                  
                  // Add name to our path
                  setCategoryNames(prev => ({
                    ...prev,
                    level4: level4Category.name
                  }));
                }
              }
            }
          }
        }
      }
      
      // Update the selected path based on the names we now know
      const pathNames = [];
      if (categoryNames.level2) pathNames.push(categoryNames.level2);
      if (categoryNames.level3) pathNames.push(categoryNames.level3);
      if (categoryNames.level4) pathNames.push(categoryNames.level4);
      setSelectedPath(pathNames);
    };
    loadCategories();
  }, [fetchCategories, categoryIds.level2, categoryIds.level3, categoryIds.level4]);

  // This state will track which item was recently selected to show a selection animation
  const [recentlySelected, setRecentlySelected] = useState<{
    id: string | null;
    level: number | null;
  }>({ id: null, level: null });

  // Handle click on a category
  const handleCategoryClick = (category: Category, level: number) => {
    // Show selection animation
    setRecentlySelected({ id: category.id, level });
    setTimeout(() => {
      setRecentlySelected({ id: null, level: null });
    }, 500); // Reset after animation duration

    const newSelectedCategories = { ...selectedCategories };
    const newCategoryNames = { ...categoryNames };
    const newCategoryIds = { ...categoryIds };

    // Reset validation state when making any new selection
    setValidated(false);
    
    if (level === 1) {
      // Reset Level 3 and Level 4 if Level 2 changes
      if (newSelectedCategories.level2?.id !== category.id && (newSelectedCategories.level3 || newSelectedCategories.level4)) {
        setRecentlyReset("level3");
        setTimeout(() => setRecentlyReset(null), 1500);
      }
      
      newSelectedCategories.level2 = category;
      newSelectedCategories.level3 = null;
      newSelectedCategories.level4 = null;
      
      // Update category names and IDs
      newCategoryNames.level2 = category.name;
      newCategoryIds.level2 = category.id;
      delete newCategoryNames.level3;
      delete newCategoryNames.level4;
      newCategoryIds.level3 = null;
      newCategoryIds.level4 = null;
      
      setSelectedCategories(newSelectedCategories);
      setCategoryNames(newCategoryNames);
      setCategoryIds(newCategoryIds);
      setLevelTwoCategories(category.children || []);
      setLevelThreeCategories([]);
      
      // Turn on guidance to help user complete selection
      setShowGuidance(true);
      
      // Call onCategorySelected even for partial selections to properly clear localStorage
      onCategorySelected(newSelectedCategories);
    } else if (level === 2) {
      // Reset Level 4 if Level 3 changes
      if (newSelectedCategories.level3?.id !== category.id && newSelectedCategories.level4) {
        setRecentlyReset("level4");
        setTimeout(() => setRecentlyReset(null), 1500);
      }
      
      newSelectedCategories.level3 = category;
      newSelectedCategories.level4 = null;
      
      // Update category names and IDs
      newCategoryNames.level3 = category.name;
      newCategoryIds.level3 = category.id;
      delete newCategoryNames.level4;
      newCategoryIds.level4 = null;
      
      setSelectedCategories(newSelectedCategories);
      setCategoryNames(newCategoryNames);
      setCategoryIds(newCategoryIds);
      setLevelThreeCategories(category.children || []);
      
      // Call onCategorySelected even for partial selections to properly clear localStorage
      onCategorySelected(newSelectedCategories);
    } else if (level === 3) {
      newSelectedCategories.level4 = category;
      
      // Update category names and IDs
      newCategoryNames.level4 = category.name;
      newCategoryIds.level4 = category.id;
      
      setSelectedCategories(newSelectedCategories);
      setCategoryNames(newCategoryNames);
      setCategoryIds(newCategoryIds);
      
      // Do NOT close dropdown for level 3 selections anymore
      // The user must click the validate button
      onCategorySelected(newSelectedCategories);
    }

    // Build the path from the category names
    const newPath = [];
    if (newCategoryNames.level2) newPath.push(newCategoryNames.level2);
    if (newCategoryNames.level3) newPath.push(newCategoryNames.level3);
    if (newCategoryNames.level4) newPath.push(newCategoryNames.level4);
    
    setSelectedPath(newPath);
  };

  // Handle category validation
  const handleValidateCategories = () => {
    // Check if we have a complete selection through all 3 levels
    if (selectedCategories.level2 && selectedCategories.level3 && selectedCategories.level4) {
      setValidated(true);
      setShowGuidance(false);
      setDropdownOpen(false);
      setGlobalSearchTerm("");
      setShowGlobalSearch(false);
    } else {
      // Show guidance to help user complete selection
      setShowGuidance(true);
    }
  };

  // Handle search input changes
  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    level: number
  ) => {
    const newSearchTerms = [...searchTerm];
    newSearchTerms[level - 1] = e.target.value;
    setSearchTerm(newSearchTerms);
  };

  // Filter categories based on search
  const filterCategories = (categories: Category[], search: string) => {
    return categories.filter((cat) =>
      cat.name.toLowerCase().includes(search.toLowerCase())
    );
  };

  // Close dropdown without resetting selection
  const handleDropdownClose = () => {
    // Check if we can close the dropdown
    if (validated || (!selectedCategories.level2 && !selectedCategories.level3 && !selectedCategories.level4)) {
      setDropdownOpen(false);
      // Only reset search terms, but keep the selected categories
      setSearchTerm(["", "", ""]);
      setGlobalSearchTerm("");
      setShowGlobalSearch(false);
      setShowGuidance(false);
    } else {
      // Show guidance to help user complete selection
      setShowGuidance(true);
    }
  };

  // Handle close without saving - completely clear the selection
  const handleClose = () => {
    // Close the dropdown without saving changes
    setDropdownOpen(false);
    setSearchTerm(["", "", ""]);
    setGlobalSearchTerm("");
    setShowGlobalSearch(false);
    setShowGuidance(false);
    
    // Clear all category selections
    setSelectedCategories({ level2: null, level3: null, level4: null });
    setCategoryIds({ level2: null, level3: null, level4: null });
    setCategoryNames({});
    setSelectedPath([]);
    
    // Also notify parent component that nothing was selected
    onCategorySelected({ level2: null, level3: null, level4: null });
  };

  return (
    <div className="custom-cascader">
      <InputGroup onClick={() => setDropdownOpen(true)}>
        <Form.Control
          placeholder="Select a category"
          value={selectedPath.length > 0 ? selectedPath.join(" > ") : ""}
          readOnly
        />
      </InputGroup>
      {dropdownOpen && (
        <>
          <div className="cascader-backdrop" onClick={(e) => {
            e.preventDefault();
            // Don't close the dropdown when clicking outside
            // Instead, show guidance if selection is incomplete
            if (!validated && (selectedCategories.level2 || selectedCategories.level3 || selectedCategories.level4)) {
              setShowGuidance(true);
            }
          }} />
          <div className="cascader-dropdown">
            {isLoadingInitial && (
              <div className="p-3 d-flex justify-content-center w-100">
                <div className="spinner-border text-warning" role="status">
                  <span className="visually-hidden">Loading categories...</span>
                </div>
              </div>
            )}
            
            {/* Global Search */}
            <div className="global-search-container p-3 border-bottom">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faSearch} className="me-2 text-muted" />
                <Form.Control
                  type="text"
                  placeholder="Search any category..."
                  value={globalSearchTerm}
                  onChange={handleGlobalSearchChange}
                  className="global-search-input"
                />
              </div>
              
              {/* Global Search Results */}
              {showGlobalSearch && globalSearchResults.length > 0 && (
                <div className="global-search-results mt-2">
                  <small className="text-muted d-block mb-2">
                    Found {globalSearchResults.length} results:
                  </small>
                  <ListGroup className="global-search-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {globalSearchResults.slice(0, 10).map((result, index) => (
                      <ListGroup.Item
                        key={`${result.category.id}-${index}`}
                        action
                        onClick={() => handleGlobalSearchSelection(result)}
                        className="d-flex justify-content-between align-items-start py-2"
                        style={{ cursor: 'pointer' }}
                      >
                        <div>
                          <div className="fw-bold">{result.category.name}</div>
                          <small className="text-muted">
                            {result.path.map(cat => cat.name).join(' > ')}
                          </small>
                        </div>
                        <span className="badge bg-secondary">Level {result.level - 1}</span>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                  {globalSearchResults.length > 10 && (
                    <small className="text-muted">
                      Showing first 10 results. Type more characters to narrow down.
                    </small>
                  )}
                </div>
              )}
              
              {showGlobalSearch && globalSearchResults.length === 0 && globalSearchTerm.length >= 2 && (
                <div className="mt-2">
                  <small className="text-muted">No categories found matching "{globalSearchTerm}"</small>
                </div>
              )}
            </div>

            {showGuidance && (
              <div className="cascader-guidance">
                <FontAwesomeIcon icon={faExclamationTriangle} className="guidance-icon" />
                <span>Please select a category at each level and validate your selection</span>
              </div>
            )}
            
            {!showGlobalSearch && (
              <div className="cascader-menus">
                {/* Level 1 */}
                <div className="cascader-menu">
                  <Form.Control
                    type="text"
                    placeholder="Search level 1..."
                    value={searchTerm[0]}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleSearchChange(e, 1)
                    }
                  />
                  <ul>
                    {loading.level2
                      ? Array(5)
                          .fill(null)
                          .map((_, i) => (
                            <li key={i}>
                              <Skeleton height={20} />
                            </li>
                          ))
                      : filterCategories(categories, searchTerm[0]).map((cat) => (
                          <li
                            key={cat.id}
                            onClick={() => handleCategoryClick(cat, 1)}
                            className={`${selectedCategories.level2?.id === cat.id ? "selected" : ""} 
                                        ${recentlySelected.id === cat.id && recentlySelected.level === 1 ? "selecting" : ""}`}
                          >
                            {cat.name}
                            {cat.children && cat.children.length > 0 && (
                              <FontAwesomeIcon icon={faAngleRight} />
                            )}
                          </li>
                        ))}
                  </ul>
                </div>

                {/* Level 2 */}
                {selectedCategories.level2 &&
                  selectedCategories.level2.children &&
                  selectedCategories.level2.children.length > 0 && (
                    <div className="cascader-menu">
                      <Form.Control
                        type="text"
                        placeholder="Search level 2..."
                        value={searchTerm[1]}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleSearchChange(e, 2)
                        }
                      />
                      <ul>
                        {loading.level3
                          ? Array(5)
                              .fill(null)
                              .map((_, i) => (
                                <li key={i}>
                                  <Skeleton height={20} />
                                </li>
                              ))
                          : filterCategories(
                              levelTwoCategories,
                              searchTerm[1]
                            ).map((cat) => (
                              <li
                                key={cat.id}
                                onClick={() => handleCategoryClick(cat, 2)}
                                className={`${selectedCategories.level3?.id === cat.id ? "selected" : ""} 
                                          ${recentlySelected.id === cat.id && recentlySelected.level === 2 ? "selecting" : ""}`}
                              >
                                {cat.name}
                                {cat.children && cat.children.length > 0 && (
                                  <FontAwesomeIcon icon={faAngleRight} />
                                )}
                              </li>
                            ))}
                      </ul>
                    </div>
                  )}

                {/* Level 3 */}
                {selectedCategories.level3 &&
                  selectedCategories.level3.children &&
                  selectedCategories.level3.children.length > 0 && (
                    <div className={`cascader-menu ${recentlyReset === "level4" ? "menu-highlight-reset" : ""}`}>
                      <Form.Control
                        type="text"
                        placeholder="Search level 3..."
                        value={searchTerm[2]}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleSearchChange(e, 3)
                        }
                      />
                      <ul>
                        {loading.level4
                          ? Array(5)
                              .fill(null)
                              .map((_, i) => (
                                <li key={i}>
                                  <Skeleton height={20} />
                                </li>
                              ))
                          : filterCategories(
                              levelThreeCategories,
                              searchTerm[2]
                            ).map((cat) => (
                              <li
                                key={cat.id}
                                onClick={() => handleCategoryClick(cat, 3)}
                                className={`${selectedCategories.level4?.id === cat.id ? "selected" : ""} 
                                          ${recentlySelected.id === cat.id && recentlySelected.level === 3 ? "selecting" : ""}`}
                              >
                                {cat.name}
                              </li>
                            ))}
                      </ul>
                    </div>
                  )}
              </div>
            )}
            
            {/* Validate and Close Buttons */}
            <div className="cascader-actions d-flex gap-2">
              <Button 
                variant="primary" 
                className="validate-categories-btn"
                disabled={!selectedCategories.level2 || !selectedCategories.level3 || !selectedCategories.level4} 
                onClick={handleValidateCategories}
              >
                <FontAwesomeIcon icon={faCheck} className="me-2" />
                Validate Categories
              </Button>
              <Button 
                variant="secondary" 
                className="close-button"
                onClick={handleClose}
              >
                <FontAwesomeIcon icon={faTimes} className="me-2" />
                Cancel
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomCascader
